import classNames from 'classnames';
import { CC_CARD_TEXT } from './utils';

export type CcProductCardGiftOption = {
  id: string;
  title: string;
  value?: string;
  /** "Top-up HK$200": a gift the reader pays towards is never shown at its bare value (FR-1.5.6). */
  topUp?: string;
  /** Computed, never authored (FR-1.5.5). */
  isHighest: boolean;
};

type CcProductCardGiftListProps = {
  id: string;
  options: CcProductCardGiftOption[];
  highestLabel: string;
  /** At most three, beneath the gifts (FR-1.5.7). */
  conditions: string[];
  promoEnds?: string;
};

/** The MoneyHero offer opened in place: every option with its value, then conditions and the end date. */
export const CcProductCardGiftList: React.FC<CcProductCardGiftListProps> = ({
  id,
  options,
  highestLabel,
  conditions,
  promoEnds,
}) => (
  <div
    id={id}
    data-component="cc-product-card-gift-list"
    className="flex flex-col gap-2.5 rounded-radius/button p-spacing/padding/soft-12px bg-fill/card"
  >
    {options.length > 0 && (
      <ul className="flex flex-col gap-spacing/gap/soft-6px">
        {options.map((option) => (
          <li
            key={option.id}
            className="flex items-start justify-between gap-spacing/gap/soft-8px"
          >
            <span className="flex min-w-0 flex-col">
              <span
                className={classNames(CC_CARD_TEXT.note, 'text-text/primary')}
              >
                {option.title}
              </span>
              {option.isHighest && (
                <span
                  className={classNames(
                    CC_CARD_TEXT.eyebrow,
                    'text-highlights/green-dark',
                  )}
                >
                  {highestLabel}
                </span>
              )}
            </span>
            {option.value && (
              <span className="flex shrink-0 flex-col items-end">
                <span
                  className={classNames(
                    CC_CARD_TEXT.value,
                    'text-text/primary',
                  )}
                >
                  {option.value}
                </span>
                {option.topUp && (
                  <span
                    className={classNames(
                      CC_CARD_TEXT.note,
                      'text-highlights/orange-dark',
                    )}
                  >
                    {option.topUp}
                  </span>
                )}
              </span>
            )}
          </li>
        ))}
      </ul>
    )}
    {(conditions.length > 0 || promoEnds) && (
      <div
        className={classNames('flex flex-col gap-spacing/gap/soft-4px', {
          'border-t border-borders/default pt-spacing/padding/soft-8px':
            options.length > 0,
        })}
      >
        {conditions.length > 0 && (
          <ul className="list-disc pl-spacing/padding/soft-12px">
            {conditions.map((condition) => (
              <li
                key={condition}
                className={classNames(
                  CC_CARD_TEXT.note,
                  'text-text/secondary-lighter',
                )}
              >
                {condition}
              </li>
            ))}
          </ul>
        )}
        {promoEnds && (
          <span
            className={classNames(
              CC_CARD_TEXT.note,
              'font-font-weight/medium text-text/primary',
            )}
          >
            {promoEnds}
          </span>
        )}
      </div>
    )}
  </div>
);
